import { Component, Injectable } from '@angular/core'
import { FormBuilder, FormGroup, ValidatorFn } from '@angular/forms'
import {Router, ActivatedRoute, CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router'
import { BaseComponent } from '../../../components/base.component'
import { CustomerActions } from '../../../actions/customer-actions'
import { IndustryActions } from '../../../actions/industry-actions'
import {Customer, Account, Industry} from '../../../types'
import { errorToast, successToast } from '../../../toaster'
import { customerGetters } from '../../../stores/customer-store'
import { possessive, isAgencySignup } from '../../../utils'
import sweetAlert from '../../../bmAlert'
import { Title } from '@angular/platform-browser'
import { runInThisContext } from 'vm'

@Component({
  selector: 'industry-page',
  template: `
    <split-view>
      <div left>
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-12 mt-3">
              <div class="nc-section-icon fas fa-comment"></div>
              <h1 class="nc-section-header">
                About {{ customer?.businessName }}
              </h1>

              <p *ngIf="!isAgencySignup()" class="top-buffer">Tell us about your business.</p>
              <p *ngIf="!isAgencySignup()" class="top-buffer">Your business and brand are unique. Tell us about your business so we can write blogs that fit your brand voice.</p>
              <p *ngIf="isAgencySignup()" class="top-buffer">Your client's business and brand are unique. Tell our writers more so they can create content that fits your client's voice.</p>
              <about-nav [customer]="customer"></about-nav>
            </div>
          </div>
        </div>
      </div>
      <div right>
          <div class="categories big-top-buffer">
            <div class="container-fluid">
              <div class="row">
                <div class="col-sm-12">
                  <h2>{{account?.agency ? possessive(customer?.businessName) : 'Your'}} Industry</h2>
                  <p>Including your client's industry will help us match you with writers who are subject matter experts.</p>
                  <p class="top-buffer bottom-buffer">{{account?.agency ? "" : "Select your industry. "}}Feel free to select more than one.</p>
                </div>
              </div>
              <div class="row">
                <div class="col-sm-12">
                  <form [formGroup]="form">
                    <label>Primary Industry</label>
                    <select formControlName="primaryIndustryId" class="form-control">
                      <option [value]="null" disabled>Select your primary industry:</option>
                      <option *ngFor="let industry of industries" [value]="industry.id">{{industry.name}}</option>
                    </select>
                    <label class="small-top-buffer">Secondary Industries</label>
                    <select formControlName="secondaryIndustryId1" class="form-control">
                      <option [value]="null">Select a secondary industry:</option>
                      <option *ngFor="let industry of industries" [value]="industry.id">{{industry.name}}</option>
                    </select>
                    <select formControlName="secondaryIndustryId2" class="form-control">
                      <option [value]="null">Select a secondary industry:</option>
                      <option *ngFor="let industry of industries" [value]="industry.id">{{industry.name}}</option>
                    </select>
                    <div *ngIf="isOtherIndustrySelected">
                      <label class="small-top-buffer">Tell us your other industries</label>
                      <tag-input  [theme]="'chips-theme'"
                                  formControlName="customIndustries"
                                  (onBlur)="setCustomIndustries()"
                                  addOnBlur='true'
                                  secondaryPlaceholder='Enter an industry'
                                  placeholder='Add an industry'
                                  >
                      </tag-input>
                      <div [hidden]="!form.errors?.customIndustries || !submitted" class="alert alert-danger small-top-buffe0r mt-1">
                        Please specify at least one industry
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <div class="row">
                <div class="col-sm-12">
                  <div class="top-buffer bottom-buffer">
                    <button (click)="onSubmit()" class="block btn btn-primary mr-2">Save & continue</button>
                    <button (click)="next()" class="block btn btn-default">Skip for now</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </div>
    </split-view>
  `,
  styles: [`
    select {
      margin-top: 6px;
      margin-bottom: 10px;
    }
  `]
})
export class IndustryPage extends BaseComponent {
  customerId: number;
  customer: Customer;
  account: Account;
  industries: Industry[] = [];
  form: FormGroup;
  saving: boolean = false;
  submitted: boolean = false;

  constructor(private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private customerActions: CustomerActions,
    private industryActions: IndustryActions,
    private titleService: Title) {
    super()
  }

  ngOnInit(): void {
    this.industryActions.loadIndustries()
    this.observe(customerGetters.industries, is => this.industries = is || [])
    this.observeAccount(a => this.account = a)

    const routeObs = this.route.params.subscribe(params => {
      this.customerId = +params['customerId']
      this.customerActions.loadCustomer(this.customerId)
      this.observeCustomer(this.customerId, c => {
        this.customer = c
        this.resetForm()
      })
    })
    this.observers.push(routeObs)
    this.titleService.setTitle('Sign Up - Industry')
  }

  resetForm(): void {
    const c = this.customer
    const formattedIndustries = c?.customIndustries.map(industry => {
      return { value: industry, display: industry }
    })
    this.form = this.formBuilder.group({
      primaryIndustryId: [c ? c.primaryIndustryId : null],
      secondaryIndustryId1: [c ? c.secondaryIndustryIds[0] : null],
      secondaryIndustryId2: [c ? c.secondaryIndustryIds[1] : null],
      customIndustries: [ formattedIndustries ],
    }, {
      validator: this.customerIndustriesValidator
    })

    if (c && !c.primaryIndustryId) {
      this.form.get('secondaryIndustryId1').disable()
      this.form.get('secondaryIndustryId2').disable()
    }
    this.form.get('primaryIndustryId').valueChanges.forEach((id) => {
      if (id) {
        this.form.get('secondaryIndustryId1').enable()
        this.form.get('secondaryIndustryId2').enable()
      }
    })
  }

  onSubmit(): void {
    this.submitted = true
    if (this.form.valid && !this.saving) {
      this.saving = true
      let customIndustries = []
      if(this.isOtherIndustrySelected) customIndustries = this.customer.customIndustries
      const body = {
        primaryIndustryId: this.form.value.primaryIndustryId,
        secondaryIndustryIds: [this.form.value.secondaryIndustryId1, this.form.value.secondaryIndustryId2].filter(id => !!id && id !== 'null'),
        customIndustries: customIndustries,
      }
      this.customerActions.updateCustomer(this.customer.id, body).then((c) => {
        successToast('Subscription updated')
        this.saving = false
        this.resetForm()
        this.next()
      })
      .catch(() => {
        errorToast('Failed to update subscription, please try again')
      })
    }
  }

  next(): void {
    this.router.navigate(['../', 'tone'], { relativeTo: this.route })
  }

  isDirty(): boolean {
    return this.form.dirty
  }

  possessive(str: string): string {
    return possessive(str)
  }

  isAgencySignup(): boolean {
    return isAgencySignup(this.account)
  }

  get otherIndustryId(): number {
    const otherInd = this.industries.find(i => i.name === 'Other')
    return otherInd ? otherInd.id : null
  }

  get isOtherIndustrySelected(): boolean {
    const formValue = this.form?.value
    if(!formValue) { return false }

    const selectedIndustries = []
    selectedIndustries.push(+formValue.primaryIndustryId)
    selectedIndustries.push(+formValue.secondaryIndustryId1)
    selectedIndustries.push(+formValue.secondaryIndustryId2)
    return this.otherIndustryId && formValue && selectedIndustries.includes(this.otherIndustryId)
  }

  customerIndustriesValidator: ValidatorFn = (g: FormGroup) => {
    if (this.otherIndustryId) {
      const otherIndustryNotAdded = !g.get('customIndustries').value?.length

      if (this.isOtherIndustrySelected && otherIndustryNotAdded) {
        return { customIndustries: { required: true } }
      } else {
        return null
      }
    } else {
      return null
    }
  }

  setCustomIndustries(): void {
    const customIndustries = this.form.value.customIndustries.map((industriesObject) => {
      return industriesObject.value
    })
    this.customer.customIndustries = customIndustries
  }
}

@Injectable()
export class CanDeactivateIndustryPage implements CanDeactivate<IndustryPage> {
  canDeactivate(component: IndustryPage, route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean>|boolean {
    if (component.isDirty() && !component.saving) {
      return sweetAlert.confirm({text: "You have unsaved changes, are you sure you want to continue?"})
    } else {
      return true
    }
  }
}
